import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useSeo } from "@/lib/seo";

const PAGE_META = {
  title: "San Francisco Golf Lessons & Coaches | GolfStart",
  description:
    "Find golf lessons in San Francisco for beginners, adults, and juniors. Explore local golf coaches and compare teaching styles, experience, and lesson options with GolfStart.",
};

const CATEGORIES = [
  {
    id: "beginner",
    title: "Beginner Golf Lessons",
    body:
      "First-time coaching focused on fundamentals — grip, setup, posture and a repeatable swing. A gentle on-ramp for anyone who's never swung a club.",
  },
  {
    id: "adult",
    title: "Adult Golf Lessons",
    body:
      "For players who want to sharpen specific parts of their game — short game, putting, course strategy or full-swing mechanics — at their own pace.",
  },
  {
    id: "junior",
    title: "Junior Golf Lessons",
    body:
      "Age-appropriate coaching for kids and teenagers, with teaching styles tuned to attention span, coordination and long-term enjoyment of the game.",
  },
  {
    id: "private",
    title: "Private Golf Lessons",
    body:
      "One-on-one sessions with undivided attention, personalised feedback and a plan built around your goals rather than a group curriculum.",
  },
];

const FAQS = [
  {
    q: "How do I find a golf coach in San Francisco?",
    a: "Browse the GolfStart coach directory, read each coach's profile, and compare their experience, teaching style, lesson format and location. Reach out to the coach whose approach best matches your goals, and consider starting with a single lesson before committing to a package.",
  },
  {
    q: "Are golf lessons good for beginners?",
    a: "Yes. Working with a coach early helps beginners build fundamentals — grip, posture, alignment, tempo — before bad habits set in. A few early lessons often prevent months of frustration and plateau later on.",
  },
  {
    q: "How many golf lessons does a beginner need?",
    a: "It varies by goals and how often you can practice between sessions. Many beginners see meaningful progress within 4–6 lessons when they practice the drills their coach assigns between sessions. The most important thing is consistency, not volume.",
  },
  {
    q: "Should I choose private or group golf lessons?",
    a: "Private lessons offer undivided attention and faster feedback loops, which many beginners prefer. Group lessons are typically more affordable and can be a fun, social way to learn. Pick based on your budget, learning style and whether you prefer focused attention or a shared environment.",
  },
  {
    q: "What should I bring to my first golf lesson?",
    a: "Comfortable athletic clothing, closed-toe shoes (golf shoes are optional for the first lesson), water, and your clubs if you have them. If you don't own clubs yet, most coaches will have loaners available — just confirm when you book.",
  },
];

function FaqSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export default function SanFranciscoGolfLessons() {
  useSeo(PAGE_META);

  return (
    <div className="min-h-screen bg-cream" data-testid="sf-lessons-page">
      <FaqSchema />
      <Header />

      {/* HERO */}
      <section className="border-b border-cream-border">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow mb-6">San Francisco — Golf Coaching</div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-forest">
                Find Golf Lessons{" "}
                <em className="italic text-gold font-light">in San Francisco.</em>
              </h1>
              <p className="mt-8 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
                Looking for the right golf coach in San Francisco? GolfStart helps golfers
                explore local coaching options based on experience, teaching style, specialty
                and lesson type.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/coaches"
                  data-testid="sf-hero-cta"
                  className="btn-primary inline-flex items-center gap-3"
                >
                  Explore Golf Coaches
                  <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                </Link>
                <Link to="/journal" data-testid="sf-hero-journal-cta" className="btn-secondary">
                  Read the Journal
                </Link>
              </div>
            </div>
            <div className="md:col-span-4 md:text-right">
              <div className="rule md:ml-auto mb-4" />
              <div className="eyebrow">Serving</div>
              <div className="mt-2 font-serif text-xl text-forest-dark">
                San Francisco &amp; the Bay Area
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1 · Golf Lessons for Every Golfer */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="max-w-2xl">
          <div className="eyebrow mb-5">01 — Options</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
            Golf lessons for{" "}
            <em className="italic text-gold font-light">every golfer.</em>
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {CATEGORIES.map((c, i) => (
            <article
              key={c.id}
              data-testid={`sf-category-${c.id}`}
              className="bg-cream-light border border-cream-border p-7"
            >
              <div className="font-serif text-3xl text-gold leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-5 font-serif text-2xl leading-tight text-forest">{c.title}</h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-forest-muted">
                {c.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* 2 · Find the Right Golf Coach */}
      <section className="bg-cream-light border-y border-cream-border">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-5">02 — The Match</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
                Find the right <em className="italic text-gold font-light">golf coach.</em>
              </h2>
              <p className="mt-6 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
                Choosing a golf coach is a personal decision. A good coach for a complete beginner
                may not be the right fit for a competitive junior, and vice versa. Weigh four or
                five things before you book: the coach&rsquo;s experience, their teaching style,
                where they teach, the lesson formats they offer, and whether their specialty fits
                your goals.
              </p>
            </div>
            <div className="lg:col-span-5 lg:pt-4">
              <div className="border border-cream-border bg-cream p-7 md:p-8">
                <div className="eyebrow">Ready to look?</div>
                <h3 className="mt-3 font-serif text-2xl md:text-3xl text-forest leading-tight">
                  Browse the GolfStart roster.
                </h3>
                <p className="mt-3 font-sans text-sm text-forest-muted leading-relaxed">
                  Compare coaches side-by-side and read each profile before you reach out.
                </p>
                <Link
                  to="/coaches"
                  data-testid="sf-explore-coaches-btn"
                  className="mt-6 btn-primary inline-flex items-center gap-2 w-full justify-center"
                >
                  Explore Golf Coaches
                  <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 · Beginner Golf Lessons */}
      <section className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="eyebrow mb-5">03 — Beginners</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
          Beginner golf lessons{" "}
          <em className="italic text-gold font-light">in San Francisco.</em>
        </h2>
        <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-forest-muted">
          Your first few golf lessons set the tone for everything that follows. A good beginner
          course doesn&rsquo;t rush you to the full-swing driver — it builds slowly and in order.
        </p>
        <ul className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-5">
          {[
            ["Fundamentals", "Grip, posture, alignment and ball position. The small things that make the swing repeatable."],
            ["Setup &amp; Swing Basics", "Learning how to balance effort and tempo rather than swinging as hard as possible."],
            ["Short Game", "Putting, chipping and pitching inside 50 yards — where most scores are won or lost."],
            ["Course Preparation", "What to expect on the first round, pace of play, and etiquette on and around the greens."],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-4 items-start">
              <Check className="h-4 w-4 text-gold mt-1.5 flex-shrink-0" strokeWidth={1.75} />
              <div>
                <div
                  className="font-serif text-xl text-forest"
                  dangerouslySetInnerHTML={{ __html: t }}
                />
                <p className="mt-1 font-sans text-sm text-forest-muted leading-relaxed">{d}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* 4 · Junior Golf Lessons */}
      <section className="bg-cream-light border-y border-cream-border">
        <div className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <div className="eyebrow mb-5">04 — Juniors</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
            Junior golf lessons.
          </h2>
          <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-forest-muted">
            Choosing coaching for a junior golfer is different from choosing coaching for
            yourself. The right coach for a seven-year-old is rarely the right coach for a
            scratch-handicap competitor — and that&rsquo;s a good thing.
          </p>
          <ul className="mt-10 space-y-5">
            {[
              ["Teaching experience with juniors", "Working with young players takes a different skill set — patience, pacing and age-appropriate language."],
              ["Communication style", "Does the coach explain concepts in a way your child engages with? A trial lesson tells you more than any resume."],
              ["Lesson environment", "Group clinics build social confidence; private lessons build technique. Many juniors benefit from a mix."],
              ["Location and schedule", "A nearby coach you'll actually drive to beats a distant one you'll cancel. Factor in school and homework."],
            ].map(([t, d]) => (
              <li key={t} className="flex gap-4 items-start">
                <Check className="h-4 w-4 text-gold mt-1.5 flex-shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="font-serif text-xl text-forest">{t}</div>
                  <p className="mt-1 font-sans text-sm text-forest-muted leading-relaxed">{d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5 · Private Golf Lessons */}
      <section className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="eyebrow mb-5">05 — Private</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
          Private golf lessons{" "}
          <em className="italic text-gold font-light">in San Francisco.</em>
        </h2>
        <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-forest-muted">
          One-on-one coaching changes the pace of learning. There&rsquo;s no waiting your turn,
          no shared instruction time, and the whole hour is built around what *you* need.
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            ["Undivided attention", "Every rep is observed and corrected, not sampled."],
            ["Personal plan", "The curriculum is yours — not borrowed from a group."],
            ["Faster feedback", "Mistakes are caught early, before they become habits."],
          ].map(([t, d]) => (
            <div key={t} className="border-t border-cream-border pt-5">
              <h3 className="font-serif text-xl text-forest">{t}</h3>
              <p className="mt-2 font-sans text-sm text-forest-muted leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 font-sans text-sm text-forest-muted leading-relaxed">
          Private lessons aren&rsquo;t automatically better than group lessons — they&rsquo;re
          different. Choose based on your learning style, your budget, and how much personal
          feedback you want per hour.
        </p>
      </section>

      {/* 6 · How to Choose a Golf Coach */}
      <section className="bg-cream-light border-y border-cream-border">
        <div className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <div className="eyebrow mb-5">06 — Decision Framework</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
            How to choose a golf coach.
          </h2>
          <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-forest-muted">
            When you&rsquo;re comparing coaches, don&rsquo;t over-weight any single factor. A
            short mental checklist works better than any ranking.
          </p>
          <ul className="mt-10 space-y-4">
            {[
              ["Experience", "How long have they coached, and who do they typically work with?"],
              ["Teaching style", "Technical and video-first, or feel-based and athletic? Pick the language that fits how you learn."],
              ["Location", "Where they teach matters as much as how they teach — a nearby coach gets more of your time."],
              ["Lesson options", "Private, group, on-course, short-game only? More formats = more ways to match."],
              ["Reviews or verified information when available", "Where profiles include verified details, read them carefully. Where they don't, start with a single lesson before committing to a package."],
            ].map(([t, d]) => (
              <li key={t} className="flex gap-4 items-start">
                <span className="font-serif text-gold text-2xl leading-none mt-0.5">·</span>
                <div>
                  <div className="font-serif text-xl text-forest">{t}</div>
                  <p className="mt-1 font-sans text-sm text-forest-muted leading-relaxed">{d}</p>
                </div>
              </li>
            ))}
          </ul>
          <Link
            to="/journal/how-to-choose-a-golf-coach"
            data-testid="sf-journal-choose-link"
            className="mt-10 inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-forest-dark hover:text-gold transition-colors"
          >
            Read the full guide in the Journal
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Link>
        </div>
      </section>

      {/* 7 · FAQ */}
      <section className="mx-auto max-w-[900px] px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="eyebrow mb-5">07 — Common Questions</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
          Frequently asked{" "}
          <em className="italic text-gold font-light">questions.</em>
        </h2>
        <div className="mt-12 divide-y divide-cream-border border-y border-cream-border">
          {FAQS.map((f, i) => (
            <details
              key={i}
              data-testid={`sf-faq-${i}`}
              className="group py-6 md:py-7"
            >
              <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                <h3 className="font-serif text-xl md:text-2xl text-forest leading-tight group-hover:text-gold transition-colors">
                  {f.q}
                </h3>
                <span className="font-serif text-2xl text-gold leading-none mt-1 group-open:rotate-45 transition-transform duration-200">
                  +
                </span>
              </summary>
              <p className="mt-4 font-sans text-base leading-relaxed text-forest-muted max-w-[52ch]">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-forest text-cream-light">
        <div className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16 py-20 md:py-28 text-center">
          <div className="eyebrow text-gold">Begin</div>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[1.05]">
            Ready to Start Your{" "}
            <em className="italic text-gold font-light">Golf Journey?</em>
          </h2>
          <p className="mt-6 max-w-xl mx-auto font-sans text-base md:text-lg leading-relaxed text-cream-light/80">
            Explore golf coaching options and find the right fit for your game.
          </p>
          <Link
            to="/coaches"
            data-testid="sf-final-cta"
            className="mt-10 inline-flex items-center gap-3 bg-gold text-forest px-8 py-4 font-sans text-xs tracking-[0.25em] uppercase hover:bg-cream-light transition-colors"
          >
            Find a Golf Coach
            <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
